import {
  AlertCircle,
  Bell,
  CheckCircle2,
  Database,
} from "@tamagui/lucide-icons";
import { useSeerRating, useHVACRawData } from "common";
import { POLLING_INTERVALS_MILLIS } from 'common/src/utils/pollingConstants';
import { t } from "i18next";
import React, { useEffect } from "react";
import { PieChart } from "react-gifted-charts";
import { useTranslation } from "react-i18next";
import {
  Text,
  XStack,
  YStack,
  View,
  getTokenValue,
  Token,
  Separator,
  XStackProps,
} from "tamagui";

import { BorderedCard } from "./BorderedCard";
import { KPICard } from "../components/KPICard";
import { useAppSync } from "../contexts/AppSyncContext";
import {
  useActiveSensorAnalytics,
  useSensorAlertAnalytics,
  useSensorDistributionAnalytics,
  useSensorErrorAnalytics,
  useSensorHealthAnalytics,
  useServiceRequestAnalytics,
} from "../hooks/useAdminAnalytics";
import { useSensorType } from "../hooks/useSensorType";

export const SensorErrorsCard = () => {
  const { sensorErrorCount } = useSensorErrorAnalytics();
  const { t } = useTranslation();

  return (
    <View flexGrow={1} flexBasis={0}>
      <KPICard
        error
        icon={AlertCircle}
        title={sensorErrorCount}
        description={t("analytics.sensorErrors")}
      />
    </View>
  );
};

export const SensorAlertsCard = () => {
  const { sensorAlertCount } = useSensorAlertAnalytics();
  const { t } = useTranslation();

  return (
    <View flexGrow={1} flexBasis={0}>
      <KPICard
        warning
        icon={Bell}
        title={sensorAlertCount}
        description={t("analytics.sensorAlerts")}
      />
    </View>
  );
};

export const ActiveSensorsCard = () => {
  const { activeSensorCount } = useActiveSensorAnalytics();
  const { t } = useTranslation();

  return (
    <View flexGrow={1} flexBasis={0}>
      <KPICard
        success
        icon={CheckCircle2}
        title={activeSensorCount}
        description={t("analytics.activeSensors")}
      />
    </View>
  );
};

export const SensorHealthCard = () => {
  const { activeSensorCount, alertingSensorCount, erroringSensorCount } =
    useSensorHealthAnalytics();
  const { t } = useTranslation();
  const total = activeSensorCount + alertingSensorCount + erroringSensorCount;

  return (
    <BorderedCard title={t("analytics.sensorHealth")}>
      <YStack gap="$3">
        <Text fontSize={24}>
          {t("analytics.sensorHealthDescription", {
            errors: erroringSensorCount,
            alerts: alertingSensorCount,
          })}
        </Text>

        <YStack gap="$2">
          <XStack justifyContent="space-between" alignItems="center">
            <Text fontSize="$3">{t("analytics.sensorHealthActive")}</Text>
            <Text fontSize="$3">{activeSensorCount}</Text>
          </XStack>
          <View height={8} backgroundColor="$gray5" borderRadius="$2">
            <View
              height={8}
              backgroundColor="$successGreen"
              borderRadius="$2"
              width={`${(activeSensorCount / total) * 100}%`}
            />
          </View>

          <XStack justifyContent="space-between" alignItems="center">
            <Text fontSize="$3">{t("analytics.sensorHealthAlerting")}</Text>
            <Text fontSize="$3">{alertingSensorCount}</Text>
          </XStack>
          <View height={8} backgroundColor="$gray5" borderRadius="$2">
            <View
              height={8}
              backgroundColor="$warning"
              borderRadius="$2"
              width={`${(alertingSensorCount / total) * 100}%`}
            />
          </View>

          <XStack justifyContent="space-between" alignItems="center">
            <Text fontSize="$3">{t("analytics.sensorHealthError")}</Text>
            <Text fontSize="$3">{erroringSensorCount}</Text>
          </XStack>
          <View height={8} backgroundColor="$gray5" borderRadius="$2">
            <View
              height={8}
              backgroundColor="$error"
              borderRadius="$2"
              width={`${(erroringSensorCount / total) * 100}%`}
            />
          </View>
        </YStack>
      </YStack>
    </BorderedCard>
  );
};

export const SensorDistributionCard = () => {
  const { sensorDistribution, totalSensors } = useSensorDistributionAnalytics();
  const { translateSensorType } = useSensorType();
  const { t } = useTranslation();

  const data = sensorDistribution.map((item) => ({
    type: translateSensorType(item.type),
    value: item.value,
    houseCount: item.houseCount,
    color: item.color,
  }));

  const chartData = data.map(({ type, value, color }) => ({
    value,
    color,
    text: type,
  }));

  const chartBackgroundColor = getTokenValue(
    "$darkBackgroundGradient" as Token,
    "color",
  );

  return (
    <BorderedCard title={t("analytics.sensorDistribution")}>
      <XStack
        paddingVertical="$7"
        gap="$8"
        alignItems="center"
        justifyContent="center"
      >
        <View
          width={200}
          height={200}
          alignItems="center"
          justifyContent="center"
        >
          <PieChart
            data={chartData}
            donut
            innerRadius={60}
            radius={100}
            innerCircleColor={chartBackgroundColor}
            centerLabelComponent={() => (
              <Text fontSize="$6" fontWeight="bold">
                {totalSensors}
              </Text>
            )}
          />
        </View>
        <YStack gap="$3" alignSelf="center">
          {data.map((item, index) => (
            <XStack
              key={index}
              alignItems="center"
              justifyContent="space-between"
              gap="$2"
            >
              <XStack alignItems="center" gap="$2">
                <View
                  width={8}
                  height={8}
                  borderRadius={5}
                  backgroundColor={item.color}
                />
                <Text maxWidth={120}>{item.type}</Text>
              </XStack>
              <Text
                backgroundColor="$transparentBorder"
                paddingHorizontal={5}
                paddingVertical={1}
                borderRadius={5}
              >
                {t("analytics.sensorDistributionHouseCount", {
                  count: item.houseCount,
                })}
              </Text>
            </XStack>
          ))}
        </YStack>
      </XStack>
    </BorderedCard>
  );
};

export const ServiceRequestsCard = () => {
  const { openServiceRequestCount, pendingServiceRequestCount } =
    useServiceRequestAnalytics();
  const { t } = useTranslation();

  return (
    <BorderedCard title={t("analytics.serviceRequests")}>
      <YStack gap="$3">
        <XStack justifyContent="space-between" alignItems="center">
          <Text fontSize="$4" color="$gray11">
            {t("analytics.serviceRequestsOpen")}
          </Text>
          <View
            backgroundColor="$successGreen"
            paddingHorizontal="$3"
            paddingVertical="$1"
            borderRadius="$2"
          >
            <Text fontSize="$3" color="white" fontWeight="bold">
              {openServiceRequestCount}
            </Text>
          </View>
        </XStack>

        <XStack justifyContent="space-between" alignItems="center">
          <Text fontSize="$4" color="$gray11">
            {t("analytics.serviceRequestsPending")}
          </Text>
          <View
            backgroundColor="$warning"
            paddingHorizontal="$3"
            paddingVertical="$1"
            borderRadius="$2"
          >
            <Text fontSize="$3" color="white" fontWeight="bold">
              {pendingServiceRequestCount}
            </Text>
          </View>
        </XStack>
      </YStack>
    </BorderedCard>
  );
};

interface HVACRawData {
  id: string;
  name: string;
  value: string | number;
  timestamp: number | null;
}

export const HVACRawDataCard = () => {
  const { sensors } = useAppSync();
  const {
    data: seerData,
    loading: isLoading,
    fetchSeerRating,
  } = useSeerRating();
  const hvacRawData = useHVACRawData(seerData);

  // Fetch SEER rating data - use first available hub
  useEffect(() => {
    const firstSensor = sensors && sensors.length > 0 ? sensors[0] : null;
    const hubId = firstSensor?.hubId;

    if (hubId) {
      fetchSeerRating(hubId);

      const interval = setInterval(() => {
        fetchSeerRating(hubId);
      }, POLLING_INTERVALS_MILLIS.HVAC);
      return () => clearInterval(interval);
    }
    return undefined;
  }, [sensors, fetchSeerRating]);

  return (
    <BorderedCard title={t("hvac.rawData")}>
      <YStack gap="$2">
        {!isLoading ? (
          !hvacRawData || hvacRawData.length === 0 ? (
            <HVACRawDataNoData />
          ) : (
            <YStack gap="$3">
              <HVACRawDataHeaders hide={isLoading} />

              {hvacRawData.map((item) => (
                <HVACRawDataRow key={item.id} item={item} />
              ))}
            </YStack>
          )
        ) : null}
      </YStack>
    </BorderedCard>
  );
};

const HVACRawDataHeaders = ({
  hide,
  ...rest
}: XStackProps & { hide: boolean }) => {
  const { t } = useTranslation();

  if (hide) {
    return null;
  }

  return (
    <XStack alignItems="center" justifyContent="space-between" {...rest}>
      <View flexGrow={0.4} flexBasis={0} paddingHorizontal="$3">
        <Text fontSize="$5.5" color="$white12">
          {t("hvac.runningDevice")}
        </Text>
      </View>
      <View flexGrow={0.35} flexBasis={0} paddingHorizontal="$3">
        <Text fontSize="$5.5" color="$white12">
          {t("hvac.data")}
        </Text>
      </View>
      <View
        flexGrow={0.25}
        flexBasis={0}
        paddingHorizontal="$3"
        alignItems="center"
      >
        <Text fontSize="$5.5" color="$white12" textAlign="center">
          {t("hvac.lastUpdated")}
        </Text>
      </View>
    </XStack>
  );
};

const HVACRawDataRow = ({ item }: { item: HVACRawData }) => {
  const { t } = useTranslation();

  const getTimestampStatus = (
    timestamp: number | null,
  ): "normal" | "warning" | "critical" => {
    if (timestamp === null) {
      return "critical";
    }

    const now = Date.now() / 1000;
    const ageInMinutes = (now - timestamp) / 60;

    if (ageInMinutes <= 5) {
      return "normal";
    } else if (ageInMinutes <= 60) {
      return "warning";
    } else {
      return "critical";
    }
  };

  const getStatusColor = (status: "normal" | "warning" | "critical") => {
    switch (status) {
      case "normal":
        return "$green10";
      case "warning":
        return "$yellow10";
      case "critical":
        return "$red10";
      default:
        return "$hgPrimary";
    }
  };

  const formatTimestamp = (timestamp: number | null) => {
    if (!timestamp) {
      return t("hvac.rawData.noData");
    }

    return new Date(timestamp * 1000).toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
  };

  return (
    <XStack
      alignItems="center"
      justifyContent="space-between"
      height={60}
      backgroundColor="$black12"
      borderWidth={1}
      borderColor="$transparentBorder"
      borderRadius="$3"
    >
      <View flexGrow={0.4} flexBasis={0} paddingHorizontal="$3.5">
        <Text
          fontSize="$7"
          fontWeight="bold"
          color="$white12"
          numberOfLines={1}
          ellipsizeMode="tail"
        >
          {item.name}
        </Text>
      </View>
      <Separator vertical height="100%" borderColor="$transparentBorder" />
      <View flexGrow={0.35} flexBasis={0} paddingHorizontal="$3">
        <Text fontSize="$7" color="$white12">
          {typeof item.value === "number"
            ? `${item.value.toFixed(2)}`
            : item.value}
        </Text>
      </View>
      <Separator vertical height="100%" borderColor="$transparentBorder" />
      <View
        flexGrow={0.25}
        flexBasis={0}
        paddingHorizontal="$3"
        alignItems="center"
      >
        <XStack
          alignItems="center"
          gap="$1"
          backgroundColor={getStatusColor(getTimestampStatus(item.timestamp))}
          paddingVertical="$1"
          paddingHorizontal="$1.5"
          borderRadius={100}
        >
          <Text fontSize="$5" fontWeight="bold" color="$white" padding="$1">
            {formatTimestamp(item.timestamp)}
          </Text>
        </XStack>
      </View>
    </XStack>
  );
};

const HVACRawDataNoData = () => {
  const { t } = useTranslation();

  return (
    <XStack
      flex={1}
      gap="$3.5"
      alignItems="center"
      justifyContent="center"
      backgroundColor="$black12"
      borderRadius="$3"
      marginTop="$-2.5"
    >
      <Database size="$2.5" />
      <Text fontSize="$7.5" fontWeight="bold" color="$white12">
        {t("hvac.rawData.emptyMessage")}
      </Text>
    </XStack>
  );
};
